import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { STUDIO } from "../../data";

const LINKS = [
  { label: "Studio", id: "about" },
  { label: "Services", id: "services" },
  { label: "Book", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#F8F6F0]/75 border-b border-[#D5CFC1]/50 py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
        data-testid="site-navbar"
      >
        <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between">
          <button
            onClick={() => go("top")}
            data-testid="nav-logo"
            className="font-serif text-2xl sm:text-3xl tracking-tight text-[#1A1A18] leading-none"
          >
            Sové<span className="text-[#7A766C]">.</span>
          </button>

          <div className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-${l.id}`}
                className="group relative text-sm tracking-wide text-[#4A4843] hover:text-[#1A1A18] transition-colors duration-300"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#1A1A18] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <button
            onClick={() => go("contact")}
            data-testid="nav-book-cta"
            className="hidden md:inline-flex items-center rounded-full bg-[#3E3B32] px-6 py-2.5 text-sm text-[#F8F6F0] hover:bg-[#1A1A18] transition-colors duration-300"
          >
            Request appointment
          </button>

          <button
            className="md:hidden p-2 -mr-2 text-[#1A1A18]"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.4} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-[#EBE7DB] flex flex-col px-6 py-6"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-3xl">Sové.</span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="font-serif text-5xl text-left text-[#1A1A18] py-2"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <div className="text-sm text-[#4A4843] space-y-1">
              <p>{STUDIO.phoneDisplay}</p>
              <p>{STUDIO.instagram}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
